import { all, call, put, takeLatest, throttle } from 'redux-saga/effects';
import { fetchClient } from '../utilities/fetchClient';
import ActionTypes from '../actions/actionTypes';
import {
  getPlasticTypesListSuccess,
  getPlasticTypesListFailure,
  getPlasticTypeSuccess,
  getPlasticTypeFailure,
  createPlasticTypeSuccess,
  createPlasticTypeFailure,
  updatePlasticTypeSuccess,
  updatePlasticTypeFailure,
  getScheduledMaintenanceSuccess,
  getScheduledMaintenanceFailure,
  getScheduledMaintenanceListSuccess,
  getScheduledMaintenanceListFailure,
  createScheduledMaintenanceSuccess,
  createScheduledMaintenanceFailure,
  updateScheduledMaintenanceSuccess,
  updateScheduledMaintenanceFailure,
  deleteScheduledMaintenanceSuccess,
  deleteScheduledMaintenanceFailure,
  resetCreateScheduledMaintenance,
  resetUpdateScheduledMaintenance,
  resetDeleteScheduledMaintenance,
  resetUpdatePlasticType,
  resetCreatePlasticType,
} from '../actions/maintenanceActions';
import Env from '../Env';
import { addToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';
import { MEDIUM_THROTTLE } from '../constants';

function* getPlasticTypesList(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url:
        'store-api/v1/plastictype?' +
        `&limit=${action.payload.limit}&offset=${action.payload.offset}` +
        `&sortDirection=${action.payload.sortDirection}&sortColumn=${action.payload.sortColumn}` +
        `&searchTerm=${action.payload.searchTerm}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(getPlasticTypesListSuccess(response));
  } catch {
    yield put(getPlasticTypesListFailure());
  }
}

function* getPlasticType(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url: `store-api/v1/plastictype/${action.payload.id}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(getPlasticTypeSuccess(response));
  } catch {
    yield put(getPlasticTypeFailure());
  }
}

function* createPlasticType(action) {
  try {
    const request = action.payload.request;

    const formData = new FormData();
    formData.append('id', request.id);
    formData.append('shortName', request.shortName);
    formData.append('description', request.description);
    formData.append('state', request.state);
    formData.append('cardImage', request.cardImage);

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
      'Content-Type': 'multipart/form-data',
    };

    yield put(addToastAlert(Alert.ADD_PLASTIC_TYPE_LOADING));
    yield call(fetchClient, {
      url: 'store-api/v1/plastictype',
      method: 'POST',
      headers: header,
      body: formData,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(createPlasticTypeSuccess());
    yield put(addToastAlert(Alert.ADD_PLASTIC_TYPE_SUCCESS, 'success'));
    yield put(resetCreatePlasticType());
  } catch (e) {
    yield put(createPlasticTypeFailure(e));
    yield put(addToastAlert(e?.response?.data?.errorMessage ?? Alert.ADD_PLASTIC_TYPE_ERROR, 'error'));
    yield put(resetCreatePlasticType());
  }
}

function* updatePlasticType(action) {
  try {
    const id = action.payload.id;
    const request = action.payload.request;

    const formData = new FormData();
    formData.append('id', request.id);
    formData.append('shortName', request.shortName);
    formData.append('description', request.description);
    formData.append('state', request.state);
    formData.append('cardImage', request.cardImage);

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
      'Content-Type': 'multipart/form-data',
    };

    yield put(addToastAlert(Alert.EDIT_PLASTIC_TYPE_LOADING));
    yield call(fetchClient, {
      url: `store-api/v1/plastictype/${id}`,
      method: 'PUT',
      headers: header,
      body: formData,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(updatePlasticTypeSuccess());
    yield put(addToastAlert(Alert.EDIT_PLASTIC_TYPE_SUCCESS, 'success'));
    yield put(resetUpdatePlasticType());
  } catch {
    yield put(updatePlasticTypeFailure());
    yield put(addToastAlert(Alert.EDIT_PLASTIC_TYPE_ERROR, 'error'));
    yield put(resetUpdatePlasticType());
  }
}

function* getScheduledMaintenance(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url: `store-api/v1/maintenanceschedule/${action.payload.id}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(getScheduledMaintenanceSuccess(response));
  } catch {
    yield put(getScheduledMaintenanceFailure());
  }
}

function* getScheduledMaintenanceList(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url:
        'store-api/v1/maintenanceschedule?' +
        `&startDate=${action.payload.startDate}&endDate=${action.payload.endDate}` +
        `&pageNumber=${action.payload.pageNumber}&pageSize=${action.payload.pageSize}` +
        `&sortDirection=${action.payload.sortDirection}&sortColumn=${action.payload.sortColumn}` +
        `&searchTerm=${action.payload.searchTerm}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(getScheduledMaintenanceListSuccess(response));
  } catch {
    yield put(getScheduledMaintenanceListFailure());
    yield put(addToastAlert(Alert.GET_SCHEDULE_ERROR, 'error'));
  }
}

function* createScheduledMaintenance(action) {
  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    yield put(addToastAlert(Alert.ADD_SCHEDULE_LOADING));
    yield call(fetchClient, {
      url: 'store-api/v1/maintenanceschedule',
      method: 'POST',
      headers: header,
      body: action.payload.request,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(createScheduledMaintenanceSuccess());
    yield put(addToastAlert(Alert.ADD_SCHEDULE_SUCCESS, 'success'));
    yield put(resetCreateScheduledMaintenance());
  } catch (e) {
    yield put(createScheduledMaintenanceFailure(e));
    yield put(addToastAlert(e?.response?.data?.errorMessage ?? Alert.ADD_SCHEDULE_ERROR, 'error'));
    yield put(resetCreateScheduledMaintenance());
  }
}

function* updateScheduledMaintenance(action) {
  try {
    const id = action.payload.id;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    yield put(addToastAlert(Alert.EDIT_SCHEDULE_REQUEST));

    yield call(fetchClient, {
      url: `store-api/v1/maintenanceschedule/${id}`,
      method: 'PUT',
      headers: header,
      body: action.payload.request,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(updateScheduledMaintenanceSuccess());
    yield put(addToastAlert(Alert.EDIT_SCHEDULE_SUCCESS, 'success'));
    yield put(resetUpdateScheduledMaintenance());
  } catch (e) {
    yield put(updateScheduledMaintenanceFailure(e));
    yield put(addToastAlert(e?.response?.data?.errorMessage ?? Alert.EDIT_SCHEDULE_ERROR, 'error'));
    yield put(resetUpdateScheduledMaintenance());
  }
}

function* deleteScheduledMaintenance(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.DELETE_SCHEDULE_REQUEST));
    const response = yield call(fetchClient, {
      url: `store-api/v1/maintenanceschedule/${action.payload.id}`,
      method: 'DELETE',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(deleteScheduledMaintenanceSuccess(response));
    yield put(addToastAlert(Alert.DELETE_SCHEDULE_SUCCESS, 'success'));
    yield put(resetDeleteScheduledMaintenance());
  } catch {
    yield put(deleteScheduledMaintenanceFailure());
    yield put(addToastAlert(Alert.DELETE_SCHEDULE_ERROR, 'error'));
    yield put(resetDeleteScheduledMaintenance());
  }
}

export default function* maintenanceSaga() {
  yield all([
    takeLatest(ActionTypes.MAINTENANCE.PLASTIC_TYPES_LIST.REQUEST, getPlasticTypesList),
    throttle(MEDIUM_THROTTLE, ActionTypes.MAINTENANCE.PLASTIC_TYPE.REQUEST, getPlasticType),
    takeLatest(ActionTypes.MAINTENANCE.CREATE_PLASTIC_TYPE.REQUEST, createPlasticType),
    takeLatest(ActionTypes.MAINTENANCE.UPDATE_PLASTIC_TYPE.REQUEST, updatePlasticType),
    takeLatest(ActionTypes.MAINTENANCE.GET_SCHEDULE.REQUEST, getScheduledMaintenance),
    takeLatest(ActionTypes.MAINTENANCE.GET_SCHEDULES_LIST.REQUEST, getScheduledMaintenanceList),
    takeLatest(ActionTypes.MAINTENANCE.CREATE_SCHEDULE.REQUEST, createScheduledMaintenance),
    takeLatest(ActionTypes.MAINTENANCE.UPDATE_SCHEDULE.REQUEST, updateScheduledMaintenance),
    takeLatest(ActionTypes.MAINTENANCE.DELETE_SCHEDULE.REQUEST, deleteScheduledMaintenance),
  ]);
}
